import { VNode, type RefObject } from 'preact';
import {
  createPortal,
  useContext,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from 'preact/compat';
import { LayerContext, LayerContextProps, LayerLevel, LayerPriority } from './LayerContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { baseStyle, styles } from './themes/LayerStyles.css';
import { useThemeInterpolations } from '../hooks/UNSAFE_useThemeInterpolations/useThemeInterpolations';
import { toUnsafeDomElement } from '#utils/PRIVATE_unsafeDomRefUtils/unsafeDomRefUtils';
import { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';

export const LOGICAL_PARENT = Symbol.for('oj-logical-parent');

type Props = {
  /**
   * The content to be re-parented using Portal
   */
  children: VNode;
  /**
   * Logical parent of the re-parented content. Typically the 'launcher' associated
   * with the popup.
   */
  logicalParentRef?: RefObject<Element> | RefObject<UnsafeDomHandle<Element>> | null;
  /**
   * Determines if this layer contains a Modal.
   */
  isModal?: boolean;
  /**
   * Determines Layer's stacking context. Components that always need to render on top of other popup
   * elements (such as Messages) should use respective priority value. The default priority level is 'popup'.
   */
  priority?: LayerPriority;
  /**
   * Determines Layer's nesting context. By default, new layers will render in the context of a parent Layer,
   * if it exists (nearestAncestor)). Some popup components (such as Dialog and Message) may need to set up
   * a new nesting context as(topLevel).
   */
  level?: LayerLevel;
};

export type LayerElement = HTMLElement & {
  [LOGICAL_PARENT]?: Element | null;
};

const Layer = ({
  children,
  logicalParentRef,
  isModal = false,
  priority = 'popup',
  level = 'nearestAncestor'
}: Props) => {
  const [layerContainer, _setLayerContainer] = useState<Element | null>(null);
  const layerContainerRef = useRef<Element | null>(null);

  const context = useContext(LayerContext);

  const setLayerContainer = (elem: Element | null) => {
    layerContainerRef.current = elem;
    _setLayerContainer(elem);
  };

  // because the children are being rendered into a portal that is out of the flow of parent dom,
  // we need to specify the colorScheme and scale class names here to pick up the correct CSS styles.
  const themeClasses = useThemeInterpolations(logicalParentRef);

  let priorityStyle = styles.popupPriorityStyle;

  switch (priority) {
    case 'dialog': {
      priorityStyle = styles.dialogPriorityStyle;
      break;
    }
    case 'messages': {
      priorityStyle = styles.messagesPriorityStyle;
      break;
    }
    case 'tooltip': {
      priorityStyle = styles.tooltipPriorityStyle;
      break;
    }
    default: {
      priorityStyle = styles.popupPriorityStyle;
      break;
    }
  }

  const classes = classNames([themeClasses, baseStyle, priorityStyle]);

  // Really need useLayoutEffect instead of useEffect here. Otherwise the re-parented
  // content does not get re-rendered with valid ref's
  useLayoutEffect(() => {
    const host =
      level === 'topLevel'
        ? context.getRootLayerHost?.(priority)
        : context.getLayerHost?.(priority);

    if (!host) return;
    const doc = host.ownerDocument ?? document;
    const layer: LayerElement = doc.createElement('div');

    layer.setAttribute('data-oj-layer', isModal ? 'modal' : 'modeless');

    if (logicalParentRef) {
      layer[LOGICAL_PARENT] = toUnsafeDomElement(logicalParentRef);
    }
    host.appendChild(layer);
    setLayerContainer(layer);

    return () => {
      if (host && layer && host.contains(layer)) {
        delete layer[LOGICAL_PARENT];
        host.removeChild(layer);
      }
      if (context.onLayerUnmount) {
        context.onLayerUnmount(layer);
      }
      setLayerContainer(null);
    };
  }, [logicalParentRef, context, level, priority, isModal]);

  // add the colorScheme and scale classes if there is a layerContainer
  if (layerContainer) {
    // use the theme interpolations to set the appropriate style classes for colorScheme and scale
    layerContainer.className = classes;
  }

  const getLayerHost = useCallback(() => {
    return layerContainer;
  }, [layerContainer]);

  const newLayerContext = useMemo(() => {
    if (context.getLayerHost) {
      return {
        getRootLayerHost: context.getRootLayerHost?.bind(null, priority),
        getLayerHost: getLayerHost.bind(null)
      };
    } else {
      return {};
    }
  }, [context.getLayerHost, context.getRootLayerHost, getLayerHost, priority]);

  return (
    <LayerContext.Provider value={newLayerContext as LayerContextProps}>
      {layerContainer && createPortal(children, layerContainer)}
    </LayerContext.Provider>
  );
};

export default Layer;
